import { Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

/**
 * Directive that emits an event when a click occurs outside the host element.
 *
 * @example
 * ```html
 * <div class="dropdown" (clickOutside)="closeDropdown()">
 *   <!-- Dropdown content -->
 * </div>
 * ```
 *
 * The directive tracks both mousedown and mouseup events to determine a true
 * "click outside". This prevents false triggers when:
 * - User starts selecting text inside the element and releases outside
 * - User drags from inside to outside
 *
 * A clickOutside event only fires when both mousedown AND mouseup occur outside
 * the element.
 *
 * ## Swallowing the triggering click
 *
 * By default (`clickOutsideCancelEvent = true`), the mouseup/click that
 * triggered `clickOutside` is fully cancelled — `preventDefault()` +
 * `stopPropagation()` — so it can never ALSO register on whatever element was
 * actually clicked. Without this, dismissing an overlay by clicking outside
 * it can silently double as a click on something behind it (a button on the
 * page, a map pin, another pin re-opening a different overlay) — the outside
 * click's real DOM target is that element, not this directive.
 *
 * This works even through `pointer-events: none` on ancestors (a common way
 * to let an overlay's empty space stay click-through for what's behind it):
 * the directive listens in the CAPTURE phase on `document`, which runs
 * before the event reaches its resolved target, so stopping it there
 * prevents the target's own listeners from ever firing — regardless of CSS
 * hit-testing. `@HostListener('document:...')` cannot express this (no
 * capture option), so this directive attaches native listeners itself.
 *
 * Pass `[clickOutsideCancelEvent]="false"` where the outside click should
 * still reach its target normally alongside dismissing — e.g. a menu where
 * clicking a different actionable item should both close this menu and
 * activate that item in one tap, rather than requiring a second click.
 *
 * ## Elements that stay mounted while "closed"
 *
 * The directive's own lifecycle (`ngOnInit`/`ngOnDestroy`) is normally what
 * scopes it to "only listening while the thing it's attached to is actually
 * open" — a host created via `@if`/`*ngIf` (a modal, a popover) or by a
 * dynamic `ViewContainerRef` only exists, and so only ever listens, while
 * shown. But an element that's always in the DOM and merely toggles a CSS
 * class for open/closed (e.g. a header dropdown) keeps this directive's
 * native document listeners attached for the component's entire lifetime —
 * with `clickOutsideCancelEvent` defaulted on, that silently swallows every
 * outside click across the whole app, all the time, not just while open.
 * For that shape, bind `[clickOutsideEnabled]` to the same condition driving
 * the open/closed CSS class so the directive is inert while closed.
 */
@Directive({
  selector: '[clickOutside]',
  standalone: true,
})
export class ClickOutsideDirective implements OnInit, OnDestroy {
  @Output() clickOutside = new EventEmitter<void>();
  @Input() clickOutsideCancelEvent = true;
  /** Set to `false` (e.g. bound to the same condition as an open/closed CSS
   *  class) to make the directive a no-op while its host isn't actually
   *  showing anything an outside click should dismiss. See the class doc
   *  comment section on elements that stay mounted while "closed". */
  @Input() clickOutsideEnabled = true;

  private mouseDownInside = false;
  /** Set when the mouseup just cancelled was an outside click -- the browser still
   *  dispatches a 'click' after it regardless, independent of that cancellation. */
  private cancelPendingClick = false;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  private readonly onDocumentMouseDown = (event: MouseEvent): void => {
    if (!this.clickOutsideEnabled) return;
    this.mouseDownInside = this.elementRef.nativeElement.contains(event.target as Node | null);
  };

  private readonly onDocumentMouseUp = (event: MouseEvent): void => {
    if (!this.clickOutsideEnabled) return;
    const mouseUpInside = this.elementRef.nativeElement.contains(event.target as Node | null);
    if (this.mouseDownInside || mouseUpInside) return;

    this.clickOutside.emit();
    if (this.clickOutsideCancelEvent) {
      event.preventDefault();
      // Plain stopPropagation, not stopImmediatePropagation: other
      // ClickOutsideDirective instances also listening on this same
      // `document` node (a modal AND a dropdown both open, say) must still
      // get their turn to see this click and decide for themselves --
      // immediate would cut them off entirely. Non-immediate still fully
      // stops the event short of whatever it would otherwise have hit.
      event.stopPropagation();
      this.cancelPendingClick = true;
    }
  };

  private readonly onDocumentClick = (event: MouseEvent): void => {
    if (!this.cancelPendingClick) return;
    this.cancelPendingClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  ngOnInit(): void {
    document.addEventListener('mousedown', this.onDocumentMouseDown, true);
    document.addEventListener('mouseup', this.onDocumentMouseUp, true);
    document.addEventListener('click', this.onDocumentClick, true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousedown', this.onDocumentMouseDown, true);
    document.removeEventListener('mouseup', this.onDocumentMouseUp, true);
    document.removeEventListener('click', this.onDocumentClick, true);
  }
}
